/*
 * Settings > Recently deleted: notes, client and job files, and archived requests removed in the last 30 days.
 * Put back uses each one's own restore. After 30 days they drop off this list.
 */
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeletedItem, DeletedList } from "@steward/shared";
import { api } from "../../api/client";
import { Icon, type IconName } from "../../components/Icon";
import { useToast } from "../../components/overlays";
import { ago, Button, Card, EmptyState, LoadError, Skeleton } from "../../components/parts";
import { errText } from "../../components/work";

const KIND: Record<DeletedItem["kind"], { word: string; icon: IconName; path: string }> = {
  note: { word: "Note", icon: "note", path: "notes" },
  file: { word: "File", icon: "file", path: "files" },
  request: { word: "Request", icon: "requests", path: "requests" },
};
const ON_WORD: Record<DeletedItem["on"], string> = { client: "a client", job: "a job", visit: "a visit", request: "" };

function Where({ i }: { i: DeletedItem }) {
  if (i.on === "client")
    return (
      <Link to="/clients/$id" params={{ id: i.onId }}>
        Open client
      </Link>
    );
  if (i.on === "job")
    return (
      <Link to="/jobs/$id" params={{ id: i.onId }}>
        Open job
      </Link>
    );
  if (i.on === "visit")
    return (
      <Link to="/visits/$ref" params={{ ref: i.onId }}>
        Open visit
      </Link>
    );
  return null;
}

export function DeletedSettings() {
  const toast = useToast();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["recently-deleted"], queryFn: () => api<DeletedList>("GET", "recently-deleted") });
  const putBack = useMutation({
    mutationFn: (i: DeletedItem) => api<unknown>("POST", `${KIND[i.kind].path}/${i.id}/restore`),
    onSuccess: (_r, i) => {
      toast(`${KIND[i.kind].word} put back`);
      void qc.invalidateQueries();
    },
    onError: (e) => toast(errText(e), { kind: "error" }),
  });
  if (list.error) return <LoadError error={list.error} retry={() => void list.refetch()} />;
  if (!list.data) return <Skeleton rows={4} />;
  const items = list.data.items;
  return (
    <Card title="Recently deleted" flush>
      <p className="card-note muted">
        Notes, files and requests you removed in the last 30 days. Put one back and it shows where it was.
      </p>
      {items.length ? (
        <table className="data">
          <tbody>
            {items.map((i) => (
              <tr key={`${i.kind}:${i.id}`}>
                <td className="deleted-what">
                  <Icon name={KIND[i.kind].icon} size={18} />
                  <span>
                    <b>{i.title}</b>
                    <span className="muted small">
                      {KIND[i.kind].word}
                      {ON_WORD[i.on] ? ` on ${ON_WORD[i.on]}` : ""} · deleted {ago(i.deletedAt)}
                    </span>
                  </span>
                </td>
                <td className="right">
                  <Where i={i} />
                </td>
                <td className="right">
                  <Button
                    type="secondary"
                    size="small"
                    loading={putBack.isPending && putBack.variables.id === i.id}
                    onClick={() => putBack.mutate(i)}
                  >
                    Put back
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState icon="check" title="Nothing deleted lately">
          When you remove a note, a file or a request, it waits here for 30 days.
        </EmptyState>
      )}
    </Card>
  );
}
